"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Search, ChartBar as BarChart3, CircleCheck as CheckCircle2, ArrowRight, Target, Users, Compass, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const localKeywords = [{
  id: "kw-1",
  keyword: "SEO Stoke-on-Trent",
  intent: "High Commercial",
  volume: "High",
  difficulty: "Medium",
  opportunity: "Direct lead capture for B2B & professional service providers."
}, {
  id: "kw-2",
  keyword: "SEO agency Stoke / Newcastle-under-Lyme",
  intent: "Transactional",
  volume: "Moderate",
  difficulty: "High",
  opportunity: "Differentiate with direct consultant access over generic agency models."
}, {
  id: "kw-3",
  keyword: "Staffordshire B2B Lead Generation",
  intent: "Commercial",
  volume: "Medium",
  difficulty: "Low",
  opportunity: "Prime positioning for industrial, ceramics, and logistics firms."
}, {
  id: "kw-4",
  keyword: "Local SEO Services Hanley & Fenton",
  intent: "Local Direct",
  volume: "Moderate",
  difficulty: "Low",
  opportunity: "Map Pack dominance for retail, trade, and regional offices."
}];
const stokeOpportunities = [{
  id: "opp-1",
  title: "Google Map Pack & Geo-Modifiers",
  subtitle: "Capture Pottery Town footfall & local service queries",
  metric: "+140%",
  description: "Optimising Google Business Profiles for Hanley, Longton, Burslem, Fenton, and Stoke town centres ensures high visibility when buyers search with explicit geographic markers.",
  badgeText: "High Impact"
}, {
  id: "opp-2",
  title: "Industrial & B2B Search Demand",
  subtitle: "Connecting Staffordshire manufacturing & distribution",
  metric: "3.2x",
  description: "Stoke's strategic A50/M6 corridor hub creates strong search volume for logistics, engineering, and manufacturing B2B terms that traditional local SEO neglects.",
  badgeText: "High Intent"
}, {
  id: "opp-3",
  title: "Competitor Content Gap",
  subtitle: "Outranking static agency sites in Staffordshire",
  metric: "68%",
  description: "Most local competitors rely on generic, outdated template pages. Implementing targeted technical structure and authoritative local content quickly wins top rankings.",
  badgeText: "Fast Win"
}];
const neighboringRegions = [{
  id: "reg-1",
  cityName: "Newcastle-under-Lyme",
  distance: "2 miles west",
  focus: "Local Business Search & Professional Services",
  path: "/LocalSEOPage",
  summary: "Integrated local authority strategies targeting the Newcastle borough commercial zone."
}, {
  id: "reg-2",
  cityName: "Stafford & Stone",
  distance: "16 miles south",
  focus: "County-wide B2B & Regional Headquarters",
  path: "/NationalSeoPage",
  summary: "Expanding local dominance into central Staffordshire enterprise corridors."
}, {
  id: "reg-3",
  cityName: "Crewe & South Cheshire",
  distance: "14 miles north-west",
  focus: "Rail, Transport & Commercial Hubs",
  path: "/TechnicalSeoPage",
  summary: "Technical search infrastructure tailored for multi-location regional operators."
}, {
  id: "reg-4",
  cityName: "Derby & East Midlands Link",
  distance: "35 miles east",
  focus: "A50 Logistics & Industrial Corridor",
  path: "/B2BSeoLeadGenerationPage",
  summary: "B2B search campaigns connecting West and East Midlands manufacturing hubs."
}];
const strategyPillars = [{
  id: "strat-1",
  number: "01",
  title: "Granular Local Keyword Mapping",
  text: "Targeting specific postcode sectors (ST1 to ST12) alongside high-intent commercial terms to capture qualified regional leads."
}, {
  id: "strat-2",
  number: "02",
  title: "Staffordshire Entity & Citation Building",
  text: "Establishing unambiguous regional entity signals with legitimate local directories, press mentions, and geo-targeted schema markup."
}, {
  id: "strat-3",
  number: "03",
  title: "Direct Specialist Execution",
  text: "You work directly with me, Yassine Essaadi. No junior account managers or generic monthly reporting templates."
}];
export default function StokeAnalysis() {
  const [activeKeyword, setActiveKeyword] = useState<string>(localKeywords[0].id);
  const shouldReduceMotion = useReducedMotion();
  const selectedKeywordObj = localKeywords.find(k => k.id === activeKeyword) || localKeywords[0];
  return <section id="stokelocalseoanalysis" data-nav="light" className="bg-background text-foreground py-16 md:py-24 transition-colors">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
          <Badge variant="secondary" className="mb-4 px-3 py-1 font-medium text-xs tracking-wider uppercase bg-secondary text-secondary-foreground border border-border">
            <MapPin className="size-3.5 text-primary" /> Stoke-on-Trent Local Market Insights
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight font-default text-foreground mb-6">
            Stoke-on-Trent Local SEO Analysis &amp; Growth Potential
          </h2>

          <p className="text-muted-foreground text-base md:text-lg leading-relaxed font-sans">
            As an independent freelance SEO consultant, I conduct deep, location-specific research into Stoke-on-Trent search trends. Here is how local businesses in The Potteries can capture qualified leads, outrank regional competitors, and build sustainable local search dominance.
          </p>
        </div>

        {/* Stoke SEO Opportunities */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {stokeOpportunities.map((item, i) => <div data-index={i} key={item.id}>
              <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }} className="h-full">
                <Card className="h-full bg-card text-card-foreground border border-border/80 shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-200">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="outline" className="text-xs font-semibold text-primary border-primary/30 bg-primary/5">
                        {item.badgeText}
                      </Badge>
                      <span className="text-2xl font-bold font-mono text-primary">{item.metric}</span>
                    </div>
                    <CardTitle className="text-xl font-semibold font-default text-foreground">{item.title}</CardTitle>
                    <CardDescription className="text-xs text-muted-foreground font-sans">{item.subtitle}</CardDescription>
                  </CardHeader>
                  <CardContent className="text-sm text-foreground/90 leading-relaxed font-sans">
                    {item.description}
                  </CardContent>
                </Card>
              </motion.div>
            </div>)}
        </div>

        {/* Interactive Stoke Keyword & Search Intent Hub */}
        <div className="bg-card border border-border rounded-2xl p-6 md:p-10 shadow-sm mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            
            {/* Keyword Selector */}
            <div className="lg:col-span-6 space-y-6">
              <div>
                <div className="flex items-center gap-2 text-primary font-semibold text-sm mb-2">
                  <Search className="size-4" />
                  <span>Stoke-on-Trent Keyword Dynamics</span>
                </div>
                <h3 className="text-2xl font-bold font-default text-foreground">
                  High-Value Search Intent in The Potteries
                </h3>
                <p className="text-muted-foreground text-sm mt-2 font-sans">
                  Select key local search clusters to see how targeted optimization unlocks high-intent buyers across Stoke-on-Trent.
                </p>
              </div>

              <div className="space-y-3">
                {localKeywords.map((kw, i) => {
                const isActive = kw.id === activeKeyword;
                return <div data-index={i} key={kw.id}>
                      <Button type="button" variant="ghost" onClick={() => setActiveKeyword(kw.id)} className={`w-full h-auto text-left p-4 rounded-xl border transition-colors flex items-center justify-between whitespace-normal text-wrap font-normal ${isActive ? "bg-primary text-primary-foreground border-primary shadow-xs hover:bg-primary hover:text-primary-foreground" : "bg-background text-foreground border-border hover:bg-accent/50 hover:border-primary/50"}`}>
                        <div className="space-y-1 text-left">
                          <p className="font-semibold text-sm font-default leading-tight">{kw.keyword}</p>
                          <p className={`text-xs ${isActive ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                            Search Intent: {kw.intent}
                          </p>
                        </div>
                        <ArrowRight className={`size-4 shrink-0 transition-transform ${isActive ? "translate-x-1 text-primary-foreground" : "text-muted-foreground"}`} />
                      </Button>
                    </div>;
              })}
              </div>
            </div>

            {/* Keyword Breakdown Details */}
            <div className="lg:col-span-6 bg-background rounded-xl p-6 border border-border flex flex-col justify-between h-full">
              <div className="space-y-6">
                <div className="flex items-center justify-between pb-4 border-b border-border">
                  <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Keyword Spotlight</span>
                  <Badge variant="secondary" className="text-xs bg-secondary text-secondary-foreground">
                    <BarChart3 className="size-3 mr-1" /> Verified Data Focus
                  </Badge>
                </div>

                <div>
                  <h4 className="text-xl font-bold font-default text-foreground mb-2">
                    &quot;{selectedKeywordObj.keyword}&quot;
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed font-sans">
                    {selectedKeywordObj.opportunity}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4 py-2">
                  <div className="p-3 bg-muted/50 rounded-lg border border-border/60">
                    <p className="text-xs text-muted-foreground mb-1">Search Volume</p>
                    <p className="text-sm font-bold font-mono text-foreground">{selectedKeywordObj.volume}</p>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg border border-border/60">
                    <p className="text-xs text-muted-foreground mb-1">Organic Difficulty</p>
                    <p className="text-sm font-bold font-mono text-foreground">{selectedKeywordObj.difficulty}</p>
                  </div>
                </div>

                <div className="p-4 bg-primary/5 rounded-lg border border-primary/20 space-y-2">
                  <div className="flex items-center gap-2 text-primary text-xs font-semibold uppercase tracking-wider">
                    <Target className="size-3.5" /> Recommended Strategy
                  </div>
                  <p className="text-xs text-foreground/90 leading-relaxed font-sans">
                    Build dedicated local service landing pages combined with structured schema markup, local case study proof, and hyper-targeted citations from Staffordshire business registers.
                  </p>
                </div>
              </div>

              <div className="pt-6 mt-6 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <span className="text-xs text-muted-foreground font-sans">
                  Direct consultant execution by Yassine Essaadi
                </span>
                <Link to="/ContactPage">
                  <Button size="sm" variant="default">
                    Discuss Stoke SEO Strategy <ArrowRight className="size-3.5" />
                  </Button>
                </Link>
              </div>
            </div>

          </div>
        </div>

        {/* Why Freelance Over Agency in Stoke */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center mb-16">
          <div className="lg:col-span-5 space-y-4">
            <Badge variant="outline" className="text-xs text-primary border-primary/30">
              <Users className="size-3 mr-1" /> Freelance Consultant vs Agency
            </Badge>
            <h3 className="text-2xl md:text-3xl font-bold font-default text-foreground">
              Direct Consultant Access For Stoke Businesses
            </h3>
            <p className="text-muted-foreground text-sm leading-relaxed font-sans">
              Unlike traditional marketing agencies that assign account managers and generic monthly retainers, you work directly with me, Yassine Essaadi.
            </p>
            <p className="text-muted-foreground text-sm leading-relaxed font-sans">
              I handle every technical audit, keyword architecture map, and content strategy personally. You get clear deliverables, transparent reporting, and honest measurement tailored specifically to your Staffordshire enterprise.
            </p>
            <div className="pt-2">
              <Link to="/AboutMePage">
                <Button variant="outline" size="sm" className="gap-2">
                  Learn More About Me <ArrowRight className="size-3.5" />
                </Button>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-3 gap-4">
            {strategyPillars.map((pillar, i) => <div data-index={i} key={pillar.id}>
                <Card className="bg-card text-card-foreground border border-border/80 h-full p-5 flex flex-col justify-between hover:border-primary/40 transition-colors">
                  <div>
                    <span className="text-2xl font-bold font-mono text-primary/40 block mb-3">{pillar.number}</span>
                    <h4 className="text-base font-semibold font-default text-foreground mb-2">{pillar.title}</h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">{pillar.text}</p>
                  </div>
                </Card>
              </div>)}
          </div>
        </div>

        {/* Cross-Regional Staffordshire & Midlands Link */}
        <div className="bg-muted/40 rounded-2xl p-6 md:p-10 border border-border mb-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
            <div>
              <div className="flex items-center gap-2 text-primary font-semibold text-xs tracking-wider uppercase mb-1">
                <Compass className="size-4" /> Regional Coverage
              </div>
              <h3 className="text-2xl font-bold font-default text-foreground">
                Geographically-Relevant Regional Pages
              </h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-2xl font-sans">
                Search dynamics span across county boundaries. Explore my targeted local and regional SEO consultancy strategies across neighboring West and East Midlands commercial hubs.
              </p>
            </div>

            <Link to="/LocalSEOPage">
              <Button variant="outline" size="sm" className="shrink-0">
                All Local SEO Services <ArrowRight className="size-3.5" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {neighboringRegions.map((region, i) => <div data-index={i} key={region.id}>
                <Card className="bg-card text-card-foreground border border-border/80 h-full flex flex-col justify-between hover:border-primary/50 transition-all duration-200">
                  <CardHeader className="p-4 pb-2">
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                      <span className="font-semibold text-primary">{region.cityName}</span>
                      <span className="text-[13px] font-mono">{region.distance}</span>
                    </div>
                    <CardTitle className="text-sm font-semibold font-default text-foreground">
                      {region.focus}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 pt-0 space-y-3">
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {region.summary}
                    </p>
                    <Separator />
                    <Link to={region.path} className="inline-flex items-center text-xs font-semibold text-primary hover:underline gap-1">
                      Explore Page <ArrowRight className="size-3" />
                    </Link>
                  </CardContent>
                </Card>
              </div>)}
          </div>
        </div>

        {/* CTA Banner */}
        <div className="relative rounded-2xl bg-primary text-primary-foreground p-8 md:p-12 overflow-hidden shadow-lg">
          <div className="relative z-10 max-w-3xl mx-auto text-center space-y-6">
            <Badge variant="secondary" className="bg-primary-foreground/10 text-primary-foreground border-primary-foreground/20 text-xs px-3 py-1">
              <Sparkles className="size-3.5 mr-1" /> Stoke-on-Trent SEO Consultation
            </Badge>

            <h3 className="text-xl sm:text-2xl font-bold font-default text-primary-foreground tracking-tight">
              Ready to Capture Stoke-on-Trent Search Traffic?
            </h3>

            <p className="text-primary-foreground/90 text-sm md:text-base leading-relaxed max-w-2xl mx-auto font-sans">
              Book a free SEO consultation directly with me. I will review your current Stoke rankings, identify technical bottlenecks, and outline a tailored search growth plan.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
              <Link to="/ContactPage">
                <Button size="lg" variant="secondary" className="w-full sm:w-auto font-semibold">
                  Book a Free SEO Consultation
                </Button>
              </Link>
              <Link to="/CaseStudiesPage">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                  Explore Genuine Case Studies
                </Button>
              </Link>
            </div>

            <div className="pt-4 flex items-center justify-center gap-6 text-xs text-primary-foreground/80 flex-wrap">
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="size-3.5" /> Direct Consultant Access
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="size-3.5" /> No Lock-in Retainers
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="size-3.5" /> Honest Measurement
              </span>
            </div>
          </div>
        </div>

      </div>
    </section>;
}